<template>
  <div class="card">
    <!-- 日期篩選 -->
    <div class="date-buttons ps-4 pe-4">
      <div class="button-row align-items-center">
        <button
          v-for="option in dateOptions"
          :key="option.value"
          @click="setDateRange(option.value)"
          :class="{ active: activeDateRange === option.value }"
          class="btn btn-date-range"
        >
          {{ option.label }}
        </button>
        <div class="dropdown custom-date-dropdown">
          <button class="btn btn-date-range" @click="toggleCustomRange" :class="{ active: activeDateRange === 'custom' }">
            Custom date range
            <i class="fa fa-caret-down"></i>
          </button>
          <div v-if="showCustomRange" class="dropdown-content">
            <Datepicker1 v-model="customFromDate" :format="format" :enable-time-picker="false" placeholder="From" />
            <Datepicker1 v-model="customToDate" :format="format" :enable-time-picker="false" placeholder="To" />
            <button class="btn btn-green btn-sm" @click="applyCustomRange">Apply</button>
          </div>
        </div>

        <div class="ms-auto">
          <button class="clear-filter" @click="resetFilters">Clear all filter</button>
        </div>
      </div>
    </div>

    <!-- 篩選選項 -->
    <div class="filter-row ps-4 pe-4 align-items-center">
      <!-- 狀態篩選 -->
      <div class="filter-dropdown">
        <button class="dropdown-btn" @click="toggleStateDropdown">
          State <span v-if="tempSelectedState">:{{ tempSelectedState }}</span> 
          <i class="fa fa-caret-down"></i>
        </button>
        <div v-if="showStateDropdown" class="dropdown-content">
          <button @click="setStateFilter('')" :class="{ active: tempSelectedState === '' }">All</button>
          <button
            v-for="state in states"
            :key="state"
            @click="setStateFilter(state)"
            :class="{ active: tempSelectedState === state }"
          >
            {{ state }}
          </button>
          <div class="dropdown-footer">
            <button class="btn-cancel" @click="cancelStateFilter">Cancel</button>
            <button class="btn-ok" @click="confirmStateFilter">OK</button>
          </div>
        </div>
      </div>

      <!-- 付款方式篩選 -->
      <div class="filter-dropdown">
        <button class="dropdown-btn" @click="togglePaymentDropdown">
          Payment method<span v-if="tempSelectedPaymentMethod">:{{ tempSelectedPaymentMethod }}</span> 
          <i class="fa fa-caret-down"></i>
        </button>
        <div v-if="showPaymentDropdown" class="dropdown-content">
          <button @click="setPaymentMethod('')" :class="{ active: tempSelectedPaymentMethod === '' }">All</button>
          <button @click="setPaymentMethod('Cash')" :class="{ active: tempSelectedPaymentMethod === 'Cash' }">Cash</button>
          <button @click="setPaymentMethod('Bank transfer')" :class="{ active: tempSelectedPaymentMethod === 'Bank transfer' }">Bank Transfer</button>
          <div class="dropdown-footer">
            <button class="btn-cancel" @click="cancelPaymentMethod">Cancel</button>
            <button class="btn-ok" @click="confirmPaymentMethod">OK</button>
          </div>
        </div>
      </div>

      <!-- Inactive Period 篩選 -->
      <div class="filter-dropdown">
        <button class="dropdown-btn" @click="toggleInactivePeriodDropdown">
          Unused terminals<span v-if="tempSelectedInactivePeriod">:{{ tempSelectedInactivePeriod }}</span> 
          <i class="fa fa-caret-down"></i>
        </button>
        <div v-if="showInactivePeriodDropdown" class="dropdown-content">
          <button @click="setInactivePeriod('')" :class="{ active: tempSelectedInactivePeriod === '' }">All</button>
          <button @click="setInactivePeriod('1_week')" :class="{ active: tempSelectedInactivePeriod === '1_week' }">1 week</button>
          <button @click="setInactivePeriod('2_week')" :class="{ active: tempSelectedInactivePeriod === '2_week' }">2 weeks</button>
          <button @click="setInactivePeriod('3_week')" :class="{ active: tempSelectedInactivePeriod === '3_week' }">3 weeks</button>
          <button @click="setInactivePeriod('4_week')" :class="{ active: tempSelectedInactivePeriod === '4_week' }">4 weeks</button>
          <div class="dropdown-footer">
            <button class="btn-cancel" @click="cancelInactivePeriod">Cancel</button>
            <button class="btn-ok" @click="confirmInactivePeriod">OK</button>
          </div>
        </div>
      </div>

      <div class="filter-dropdown">
        <button class="dropdown-btn" @click="toggleFleetDropdown">
          Fleet <span v-if="tempSelectedFleet">:{{ getFleetName(tempSelectedFleet) }}</span> 
          <i class="fa fa-caret-down"></i>
        </button>
        <div v-if="showFleetDropdown" class="dropdown-content">
          <button @click="setFleet('')" :class="{ active: tempSelectedFleet === '' }">All</button>
          <button v-for="fleet in fleetStore.allFleets"  @click="setFleet(fleet.id)" :class="{ active: tempSelectedFleet === fleet.id }">{{ fleet.name }}</button>
          <div class="dropdown-footer">
            <button class="btn-cancel" @click="cancelFleet">Cancel</button>
            <button class="btn-ok" @click="confirmFleet">OK</button>
          </div>
        </div>
      </div>

      <div class="search-box ms-auto position-relative">
        <input
          type="text"
          class="form-control"
          placeholder="Search..."
          v-model="terminalStore.filters.search"
          @input="onSearchInput"
        />
        <Icon
          :icon="searchIcon"
          width="30"
          height="30"
          class="search-icon"
          @click="onSearchIconClick"
        />
      </div>
    </div>

    <!-- 資料表格 -->
    <div class="mt-1" v-if="paginatedData.length > 0">
      <div class="card-body">
        <div class="table-responsive signal-table">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>State</th>
                <th>Terminal number</th>
                <th>Driver ID</th>
                <th>Fleet ID</th>
                <th>Driver name</th>
                <th>Payment method</th>
                <th>Face value</th>
                <th>Last transaction date/time</th>
                <th>Inactive time</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in paginatedData" :key="index">
                <td>{{ item.state }}</td>
                <td>{{ item.tid }}</td>
                <td>{{ item.driver_id }}</td>
                <td>{{ item.fleet_name }}</td>
                <td>{{ item.driver_name }}</td>
                <td>{{ item.payment_method }}</td>
                <td>{{ item.total_face_value }}</td>
                <td>{{ item.last_transaction }}</td>
                <td>{{ item.inactive_days }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分頁 -->
        <div class="pagination-wrapper">
          <div class="pagination-container">
            <button @click="changePage(1)" :disabled="currentPage === 1"><img  src="@/assets/images/admin/arrow/prev-end.png" alt="Prev-end" style="width: 25px; height: 25px;" /></button>
            <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1"><img  src="@/assets/images/admin/arrow/prev.png" alt="Prev" style="width: 25px; height: 25px;" /></button>
            <span>
              Page
              <input type="number" style="width: 60px;" v-model.number="currentPageInput" @change="changePage(currentPageInput)" />

              of {{ totalPages }}
            </span>
            <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages"><img  src="@/assets/images/admin/arrow/next.png" alt="Next" style="width: 25px; height: 25px;" /></button>
            <button @click="changePage(totalPages)" :disabled="currentPage === totalPages"><img  src="@/assets/images/admin/arrow/next-end.png" alt="Next-end" style="width: 25px; height: 25px;" /></button>
            <!-- <img src="@/assets/svg/left.svg" alt="downland" style="width: 25px; height: 25px;"@click="downloadCSV" > -->
          <button @click="downloadCSV" >
            <img src="@/assets/svg/left.svg" alt="downland" style="width: 25px; height: 25px;"></img>
          </button>
          </div>
          <div class="total-count">
            Displaying  {{ (currentPage - 1) * itemsPerPage + 1 }} – {{ Math.min(currentPage * itemsPerPage, totalEntries) }} of {{ totalEntries }}
          </div>
        </div>
      </div>
    </div>
    <div v-else class="no-results">No results found.</div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount,watch } from 'vue';
import { useTerminalPerformanceStore } from '@/store/admin/terminalPerformance';
import { useTerminalsStore } from '@/store/admin/terminal';
import { useFleetStore } from '@/store/admin/fleet';
import Datepicker1 from '@vuepic/vue-datepicker';
import { useRoute, useRouter } from 'vue-router';
import { Icon } from '@iconify/vue'
import '@vuepic/vue-datepicker/dist/main.css';

// Pinia store
const terminalStore = useTerminalPerformanceStore();
const terminaldataStore = useTerminalsStore();
const fleetStore = useFleetStore();
const route = useRoute();
const router = useRouter();


// 預設日期範圍選項
const dateOptions = [
  { label: 'This month', value: 'this_month' },
  { label: 'Last month', value: 'last_month' },
  { label: 'This week', value: 'this_week' },
  { label: 'Last week', value: 'last_week' },
  { label: 'This year', value: 'this_year' },
  { label: 'Last year', value: 'last_year' },
];

// 狀態選項，包括 "All"
const states = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'NT', 'TAS', 'ACT'];

const activeDateRange = ref('this_month');
const customFromDate = ref('');
const customToDate = ref('');
const showCustomRange = ref(false);
const selectedState = ref('');
const tempSelectedState = ref(''); // 用來暫存狀態篩選的變數
const selectedPaymentMethod = ref('');
const tempSelectedPaymentMethod = ref(''); // 暫存付款方式篩選的變數
const selectedInactivePeriod = ref(route.query.inactivePeriod || '');
const tempSelectedInactivePeriod = ref(''); // 暫存Inactive Period篩選變數
const selectedFleet = ref('');
const tempSelectedFleet = ref('');
const showStateDropdown = ref(false);
const showPaymentDropdown = ref(false);
const showInactivePeriodDropdown = ref(false);
const showFleetDropdown = ref(false);
const searchQuery = ref('')


// 初始化狀態的函數
const resetFilters = () => {
  activeDateRange.value = 'this_month';
  customFromDate.value = '';
  customToDate.value = '';
  selectedState.value = '';
  tempSelectedState.value = '';
  selectedPaymentMethod.value = '';
  tempSelectedPaymentMethod.value = '';
  selectedInactivePeriod.value = '';
  tempSelectedInactivePeriod.value = '';
  selectedFleet.value = '';
  tempSelectedFleet.value = '';
  terminalStore.resetState(); // 重置 Pinia 狀態
  showCustomRange.value = false;
  showStateDropdown.value = false;
  showPaymentDropdown.value = false;
  showInactivePeriodDropdown.value = false;
  showFleetDropdown.value = false;
  terminalStore.fetchTerminalPerformances();
};


const format = (date) => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

// 切換自定義日期範圍顯示
const toggleCustomRange = () => {
  showCustomRange.value = !showCustomRange.value;
};

// 設置日期範圍
const setDateRange = (range) => {
  activeDateRange.value = range;
  terminalStore.period = range;
  terminalStore.fetchTerminalPerformances();
};

// 自訂日期範圍應用
const applyCustomRange = () => {
  const from = customFromDate.value ? customFromDate.value.toISOString().split('T')[0] : '';
  const to = customToDate.value ? customToDate.value.toISOString().split('T')[0] : '';
  terminalStore.customStartDate = from;
  terminalStore.customEndDate = to;
  terminalStore.period = 'custom';
  terminalStore.fetchTerminalPerformances();
  showCustomRange.value = false; // 隱藏自定義範圍
  activeDateRange.value = 'custom'; // 設置自定義日期為 active
};

// 根據搜尋內容決定圖示：空時顯示放大鏡，有值時顯示叉叉
const searchIcon = computed(() =>
  terminalStore.filters.search ? 'mdi:close' : 'mdi:magnify'
)

// 當使用者輸入時呼叫（可以在此函式中進行搜尋邏輯）
const onSearchInput = () => {
  terminalStore.fetchTerminalPerformances();
}

// 點擊圖示時：若有值則清空
const onSearchIconClick = () => {
  if (terminalStore.filters.search) {
    terminalStore.filters.search = ''
    onSearchInput() // 可選：清空後也重新執行搜尋
  }
}

// 切換 Inactive Period 篩選下拉選單
const toggleInactivePeriodDropdown = () => {
  showInactivePeriodDropdown.value = !showInactivePeriodDropdown.value;
  if (showInactivePeriodDropdown.value) {
    tempSelectedInactivePeriod.value = selectedInactivePeriod.value; // 打開時存入目前的篩選值
  }
};

// 設置 Inactive Period 篩選
const setInactivePeriod = (period) => {
  tempSelectedInactivePeriod.value = period;
};

// 點擊取消 Inactive Period 篩選
const cancelInactivePeriod = () => {
  tempSelectedInactivePeriod.value = selectedInactivePeriod.value;
  showInactivePeriodDropdown.value = false;
};

// 確認 Inactive Period 篩選
const confirmInactivePeriod = async () => {
  selectedInactivePeriod.value = tempSelectedInactivePeriod.value;
  terminalStore.filters.inactivePeriod = selectedInactivePeriod.value;
  await terminalStore.fetchTerminalPerformances();
  showInactivePeriodDropdown.value = false;
};

// 切換 State 篩選下拉選單
const toggleStateDropdown = () => {
  showStateDropdown.value = !showStateDropdown.value;
  if (showStateDropdown.value) {
    tempSelectedState.value = selectedState.value; // 打開時存入目前的篩選值
  }
};

// 設置 State 篩選
const setStateFilter = (state) => {
  tempSelectedState.value = state;
};

// 點擊取消 State 篩選
const cancelStateFilter = () => {
  tempSelectedState.value = selectedState.value;
  showStateDropdown.value = false;
};

// 確認 State 篩選
const confirmStateFilter = () => {
  selectedState.value = tempSelectedState.value;
  terminalStore.filters.state = selectedState.value;
  terminalStore.fetchTerminalPerformances();
  showStateDropdown.value = false;
};

// 切換 Payment Method 篩選下拉選單
const togglePaymentDropdown = () => {
  showPaymentDropdown.value = !showPaymentDropdown.value;
  if (showPaymentDropdown.value) {
    tempSelectedPaymentMethod.value = selectedPaymentMethod.value;
  }
};

// 設置付款方式篩選
const setPaymentMethod = (method) => {
  tempSelectedPaymentMethod.value = method;
};

// 點擊取消 Payment Method 篩選
const cancelPaymentMethod = () => {
  tempSelectedPaymentMethod.value = selectedPaymentMethod.value;
  showPaymentDropdown.value = false;
};

// 確認付款方式篩選
const confirmPaymentMethod = () => {
  selectedPaymentMethod.value = tempSelectedPaymentMethod.value;
  terminalStore.filters.paymentMethod = selectedPaymentMethod.value;
  terminalStore.fetchTerminalPerformances();
  showPaymentDropdown.value = false;
};

const getFleetName = (fleetId) => {
  const fleet = fleetStore.allFleets.find(f => f.id === fleetId);
  return fleet ? fleet.name : '';
};


const setFleet = (fleet) => {
  tempSelectedFleet.value = fleet;
};

// 點擊取消 Payment Method 篩選
const cancelFleet = () => {
  tempSelectedFleet.value = selectedFleet.value;
  showFleetDropdown.value = false;
};

// 確認付款方式篩選
const confirmFleet = () => {
  selectedFleet.value = tempSelectedFleet.value;
  terminalStore.filters.fleet_id = selectedFleet.value;
  terminalStore.fetchTerminalPerformances();
  showFleetDropdown.value = false;
};


const toggleFleetDropdown = () => {
  showFleetDropdown.value = !showFleetDropdown.value;
  if (showFleetDropdown.value) {
    tempSelectedFleet.value = selectedFleet.value; // 打開時存入目前的篩選值
  }
};

// 分頁
const currentPage = ref(terminalStore.currentPage);
const itemsPerPage = ref(terminalStore.itemsPerPage);
const currentPageInput = ref(currentPage.value);

// 計算分頁資料
const paginatedData = computed(() => terminalStore.terminals);

const totalPages = computed(() => {
  return terminalStore.totalPages;
});

const totalEntries = computed(() => {
  return terminalStore.totalEntries;
});

const downloadCSV = () => {
  
  const filters = {
    period: terminalStore.period,
    state: terminalStore.filters.state,
    payment_method: terminalStore.filters.paymentMethod !== 'ALL' ? terminalStore.filters.paymentMethod : '',
    inactive_period: terminalStore.filters.inactivePeriod !== 'ALL' ? terminalStore.filters.inactivePeriod : '',
    fleet_id: terminalStore.filters.fleet_id,
    search: terminalStore.filters.search,
    custom_start: terminalStore.customStartDate,
    custom_end: terminalStore.customEndDate,
  };
  
  terminaldataStore.downloadTerminalReport(filters);
}


const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    currentPageInput.value = page;
    terminalStore.currentPage = page;
    terminalStore.fetchTerminalPerformances();
  }
};


// 當使用者離開分頁時重置篩選條件
router.beforeEach((to, from, next) => {
  if (from.path === '/terminal-performance') {
    resetFilters();
  }
  next();
});


// 當組件掛載時檢查是否有 query
onMounted(async () => {
  await terminalStore.fetchTerminalPerformances(); // 根據篩選條件獲取數據
  await fleetStore.fetchFleetForSelect();
});

onBeforeUnmount(() => {
  resetFilters(); // 當組件被卸載時重置篩選
});
</script>


<style scoped>
.card {
  margin: 20px;
  padding-top: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-body {
  padding: 20px;
}

.no-results {
  margin: 15px;
  border-radius: 20px;
  padding: 50px;
  font-size: 20px;
  text-align: center;
  color: black;
  background-color: #ccd3da;
}

.table-responsive {
  margin-top: 20px;
}

.table-hover tbody tr:hover {
  background-color: #f1f1f1;
}

.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
}

.pagination-container {
  display: flex;
  align-items: center;
}

.pagination-container button {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  color: #8476f4;
}

.pagination-container span {
  margin: 0 10px;
}

.pagination-container input {
  width: 50px;
  text-align: center;
}

.total-count {
  font-size: 16px;
  color: #666;
}

.btn-date-range {
  margin-right: 10px;
  border: 1px solid #8476f4;
  background-color: white;
  color: #8476f4;
  height: 46px;
  font-size: 16px;
  padding: 0px 24px;
  border-radius: 30px;
  cursor: pointer;
  margin-top: 5px;
}

.btn-date-range.active {
  background-color: #8476f4;
  color: white;
}

.filter-row {
  display: flex;
  margin-top: 10px;
}

.button-row {
  display: flex;
  flex-wrap: wrap;
}

.filter-dropdown {
  position: relative;
  margin-top: 10px;
}

.dropdown-btn {
  border: 1px solid #8476f4;
  background-color: white;
  margin-right: 10px;
  color: #8476f4;
  height: 46px;
  font-size: 16px;
  padding: 0px 24px;
  border-radius: 30px;
  cursor: pointer;
}

.dropdown-content {
  position: absolute;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1;
  padding: 10px;
  width: 200px;
  border-radius: 10px;
  margin-top: 5px;
}

.dropdown-content button {
  width: 100%;
  padding: 10px;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
}

.dropdown-content button.active {
  background-color: #8476f4;
  color: white;
}

.dropdown-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
}

.btn-cancel,
.btn-ok {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  color: #8476f4;
}

.clear-filter{
  border: none;
  font-size: 18px;
  color: #8476f4;
  background-color: white;
}

.clear-filter:hover{
  border-bottom: 2px solid #8476f4;
}

.search-box {
  margin-top: 10px;
  width: 250px;
}
.search-box input {
  height: 48px;
  font-size: 18px;
}
.search-box .search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #8476f4;
}

</style>
